'use client';

import { Menu, X } from 'lucide-react';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { trackEvent } from '@/utils/analytics';
import { services } from '@/utils/constants';
import { getFileUrl } from '@/utils/helpers';
import { cn } from '@/utils/utils';
import { NAVBAR_HEIGHT, NAVIGATION_LINKS } from './constants';
import { DesktopNavigation } from './desktop-navigation';
import { MobileMenu } from './mobile-menu';
import { useNavbar } from './navbar-hook';
import type { DropdownItem, NavbarProps } from './types';

export default function NavbarContainer({
  isDynamicHide = true,
  initialDelay = null,
  destinations: destinationsData,
}: NavbarProps) {
  const { isVisible } = useNavbar({
    isDynamicHide,
    initialDelay,
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const destinations: DropdownItem[] = destinationsData.map((destination) => ({
    title: destination.name,
    href: `/destinations/${destination.slug}`,
    description: destination.introduction,
    image: getFileUrl(destination.cover?.key, 'image'),
  }));

  const handleMobileMenuToggle = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    trackEvent({
      action: newState ? 'mobile_menu_open' : 'mobile_menu_close',
      category: 'navigation',
      label: 'Mobile Menu Toggle',
      value: 1,
    });
  };

  const handleLogoClick = () => {
    trackEvent({
      action: 'logo_click',
      category: 'navigation',
      label: 'Home Logo',
      value: 1,
    });
  };

  const handleCtaClick = () => {
    trackEvent({
      action: 'get_in_touch_click',
      category: 'navigation',
      label: 'Desktop Header CTA',
      value: 1,
    });
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 w-[80%] md:w-[70%] mx-auto p-3 rounded-full shadow-sm bg-white',
        'transition-all duration-500 ease-in-out transform',
        isVisible ? 'translate-y-5 opacity-100' : '-translate-y-full opacity-0',
      )}
      style={{ height: `${NAVBAR_HEIGHT}px` }}
    >
      <nav className="flex justify-between items-center h-full">
        {/* Logo */}
        <a
          href="/"
          className="rounded-md h-full flex justify-start gap-4 ms-3"
          aria-label="Home"
          onClick={handleLogoClick}
        >
          <Image
            src="/images/logo.webp"
            alt="Company Logo"
            className="h-full w-auto object-contain"
            priority
            width={100}
            height={100}
          />
          <Image
            src="/images/enlil-name.webp"
            alt="Company Name"
            className="h-full w-auto object-contain py-3.5"
            priority
            width={100}
            height={100}
          />
        </a>

        {/* Desktop Navigation */}
        <DesktopNavigation
          navigationLinks={NAVIGATION_LINKS}
          destinations={destinations}
          services={services}
        />

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={handleMobileMenuToggle}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-neutral-100"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Desktop CTA */}
        <div className="hidden xl:block">
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-3.5 border border-transparent text-sm font-medium rounded-full shadow-sm bg-accent hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-secondary transition-all duration-200 truncate"
            onClick={handleCtaClick}
          >
            Get In Touch
          </a>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navigationLinks={NAVIGATION_LINKS}
        destinations={destinations}
        services={services}
      />
    </header>
  );
}
